import { ADD_APPOINTMENT_TABS } from 'AppointmentBox/helper';
import { repeatResultSignal } from 'AppointmentBox/RepeatResultModal';
import { modalConfirmSMSChangeRef } from 'AppointmentBox/ModalConfirmSMSChange';
import aptSignal from 'AppointmentBox/signal';
import { tableLoading } from 'AppointmentList/Table';
import { blockHourTableLoading } from 'AppointmentList/Table/BlockHourTable';
import { AxiosResponse } from 'axios';
import { calendarScrollTo } from 'Calendar';
import { ANYONE_ID } from 'Calendar/anyone';
import { getStaffCalendarPage } from 'utils/staff';
import calcTo from 'Calendar/helper';
import { ICalendarItemData } from 'Calendar/types';
import { customerTableLoading } from 'CalendarFilter/CustomerSearch/AppointmentByCustomer';
import { calendarDatePickerRef, dateParamSignal } from 'CalendarFilter/DatePicker';
import { isStaleCalendarDate, toCalendarDateParam } from './calendar-refresh-date';
import { beginLocalCalendarMutation, endLocalCalendarMutation } from './local-calendar-mutation';
import { focusAppointment } from 'Calendar/appointment-focus';
import { loadingSignal } from 'components/Loading';
import { loadingPageSignal } from 'components/LoadingPage';
import { STAFF_CALENDAR_PAGE_SIZE, STAFF_CALENDAR_PAGE_ZOOM_SIZE } from 'constants/calendar';
import first from 'lodash/first';
import get from 'lodash/get';
import moment from 'utils/moment';
import qbSignal from 'QuickBooking/signal';
import { all, call, cancelled, delay, put, select, takeLatest } from 'redux-saga/effects';
import store, { RootState } from 'store';
import { ISagaFunc } from 'store/actionConfigs';
import { IShopAllSetting } from 'services/types/shop';
import { IBodyCreateStaffGroup, IBodyUpdateStaffGroup, IStaffGroupAssignment, IStaffGroupResItem } from 'services/types/staff-group';
import { IStaffGroupUI } from 'types/staff-group';
import { ICategoryUI } from 'types/category';
import { ICustomerUI } from 'types/customer';
import { IServiceUI } from 'types/service';
import { IStaffUI } from 'types/staff';
import storage from 'utils/sessionStorage';
import toast from 'utils/toast';
import { buildAffectedAppointmentsWarning } from 'utils/affected-appointments-warning';
import type { IBlockHoursResult } from 'services/types/blockHours';
import { configureMomentTimezone } from 'utils/moment';
import CallerContext from 'widgets/CallerId/context';
import CallerV2 from 'Calendar/CallerV2';
import { isCallerV2Enabled } from 'Calendar/CallerV2/feature-flag';
import { callerBookingOutcome } from 'Calendar/CallerV2/caller-booking-outcome';
import aptActions from './actions/apt';
import commActions from './actions/comm';
import shopActions from './actions/shop';
import apis from './apis';
import { mappingResToCalendarDataItem } from './helpers';
import { aptUIActions } from './reducers/apt';
import { commUIActions } from './reducers/comm';
import { customerSelectors, tableSelectors } from './selectors/apt';
import commSelectors from './selectors/comm';
import { IQuickBookingPayload } from './actions/apt';
import { IApiStaffByAppointmentParams, IApiStaffByAppointmentResData, IAppointmentSummaryCallerIdParams, ICalendarDropData, IRepeatAppointmentRes } from './types/apt';
import { IBlockHourItemData, IBlockHourResItem, IBodyAddBlockHours, IBlockHourHistoryItem, IBlockHourHistoriesQuery, IBlockHourHistoriesResponse, ITimeOffListQuery } from './types/blockHours';
import { IApiHistoryCallerIdParams, ICallerIdHistoryResData, IPendingCallerResItem, ICustomerRecentApptsResData, ICustomerApptsPageParams, ICustomerApptsPageResData } from './types/caller';
import { IApiGetQuickCommissionsParams } from './types/comm';
import { IQuickBookingServiceItem } from './types/quickBooking';
import { IAppointmentListParams, IPayloadBooking, IPayloadBookingV2, IPayloadDragADrop } from './types/request';
import { IAppointmentResItem, IAptListResPayload, IBookingAptResData, ICategoryResItem, ICustomerResItem, ISchedulerSalonResData, IServiceResItem, IStaffResItem } from './types/response';
import { sortCategoriesByIndex } from 'utils/service-order';

const mappingAppointment = (appointmentRes: IAppointmentResItem[]) => appointmentRes.map(mappingResToCalendarDataItem);

const getCalendarData: ISagaFunc<{ shopId?: string; startTime: string }> = function* ({ payload }) {
  // Phai gui kem `size`: `getStaffByAppointment` so `total > payload.size` de biet
  // con trang nao chua tai. Thieu `size` thi phep so sanh chay voi `undefined` va
  // luon sai, nen tiem dong tho hon mot trang khong bao gio tai not cac cot con lai.
  const staffSize: number = yield select((state: RootState) => state.apt.staffSize);
  yield put(aptActions.getStaffByAppointment.fetch({
    date: payload.startTime || '',
    page: 1,
    size: staffSize,
  }));
};

const mappingBlockHour = (blockHourItems: IBlockHourResItem[]) => {
  const data: ICalendarItemData[] = blockHourItems.map(o => {
    const id = o.id?.toString();
    const staffId = o.staffId?.toString();
    const startTime = moment(o.startTime, 'MM/DD/YYYY HH:mm A').format('MM-DD-YYYY HH:mm:ss');
    const endTime = moment(o.endTime, 'MM/DD/YYYY HH:mm A').format('MM-DD-YYYY HH:mm:ss');
    return ({
      id,
      type: 'block-hour',
      data: {
        id,
        staffId, startTime, endTime,
        note: o.reason || '',
      } as IBlockHourItemData,
      staffId, startTime, endTime,
    });
  });
  return data;
};

const getCalendarBlockHourList: ISagaFunc<{ shopId?: string, startTime: string }> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, payload.shopId || storage.shopId.get(), {
      startTime: payload.startTime,
      endTime: payload.startTime,
    });
    const blockHourItems = res?.data?.payload;
    if (blockHourItems) {
      yield put(aptActions.calendar.setBlockHoursData(mappingBlockHour(blockHourItems)));
    }
  } catch (error) {

  } finally {
  }
};

const getCategories: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const { cate, ser } = yield all({ cate: call(apis.getCategories, shopId), ser: call(apis.getServices, shopId) });

    const categoriesRes: ICategoryResItem[] = cate?.data?.payload;
    const servicesRes: IServiceResItem[] = ser?.data?.payload;
    if (categoriesRes && servicesRes) {
      const data: ICategoryUI[] = sortCategoriesByIndex(categoriesRes).map((o) => ({
        id: o.id?.toString(),
        name: o.categoryName,
        services: servicesRes.filter(s => s.catId === o.id).map(s => ({
          cateId: o.id?.toString(),
          id: s.id?.toString(),
          image: s.urlImage,
          name: s.serviceName,
          duration: s.duration,
          price: s.price,
        }) as IServiceUI),
      }));
      yield put(shopActions.category.success(data));
    }
  } catch (error) {

  }
};

const getStaffs = function* (shopId: string) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffsAvailable, shopId, dateParamSignal.value?.format('YYYY-MM-DD') || '');
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {

  }
};

const getPureStaffs: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffs, shopId);
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.pureStaffs.success(data));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {

  }
};

function getPageNumberByIndex(index: number, pageSize = STAFF_CALENDAR_PAGE_SIZE): number {
  if (index < 0) return 0; // Optional: you might want to handle negative indices differently
  return Math.floor(index / pageSize) + 1;
}

const getStaffsAvailable: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffsAvailable, storage.shopId.get(), payload || dateParamSignal.value?.format('YYYY-MM-DD') || '');
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.staffAvailable.success(data));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {
  }
};

const getBlockHourHistories: ISagaFunc<IBlockHourHistoriesQuery> = function* ({ payload }) {
  try {
    const res = yield call(apis.getBlockHourHistories, storage.shopId.get(), payload);
    if(res?.data?.payload) {
      yield put(shopActions.blockHourHistories.success(res.data.payload));
    }
  } catch (error) {
    yield put(shopActions.blockHourHistories.fail(error));
  }
};

const getTimeOffList: ISagaFunc<ITimeOffListQuery> = function* ({ payload }) {
  try {
    const res = yield call(apis.getAllBlockHoursAptList, storage.shopId.get(), {
      startTime: payload.startTime,
      endTime: payload.endTime,
    });
    // Always resolve (empty range => empty list) so the loading flag clears.
    yield put(shopActions.timeOffList.success(res?.data?.payload || []));
  } catch (error) {
    yield put(shopActions.timeOffList.fail(error));
  }
};

const getScheduleTimeOffSlots: ISagaFunc<{ date?: string; staffId?: string }> = function* ({ payload }) {
  try {
    const currentDate = payload?.date || dateParamSignal.value?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD');
    const staffId = payload?.staffId || '';
    const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = yield call(apis.getTimeSlot, storage.shopId.get(), currentDate, staffId);
    const timeSlots = res?.data?.payload?.timeSlots || [];
    yield put(shopActions.scheduleTimeOffSlots.success(timeSlots));
  } catch (error) {
    yield put(shopActions.scheduleTimeOffSlots.fail(error));
  }
};

const getStaffActions = function* () {
  yield getStaffs(storage.shopId.get());
};

const getCustomers: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: { customers: ICustomerResItem[] } }> = yield call(apis.getAllCustomerAutoSearchPage, shopId, '');

    const customerRes = res?.data?.payload?.customers;
    if (customerRes) {
      const data: ICustomerUI[] = customerRes.map(o => ({
        id: o.id?.toString(),
        name: o.name,
        phone: o.phone,
        point: o.point,
      }));
      yield put(shopActions.customer.success(data));
    }

  } catch (error) {

  }
};

const getSalonInfo: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getSalonInfo, shopId);
    if (res.data.payload) {
      configureMomentTimezone(res.data.payload.timeZone);
      yield put(shopActions.salonInfo.success(res.data.payload));
    }
  } catch (error) { }
};

const getShopAllSetting: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: IShopAllSetting }> = yield call(apis.getShopAllSetting, shopId);
    if (res.data.payload) {
      // res.data.payload.enableCallerV2 = true;
      yield put(shopActions.shopAllSetting.success(res.data.payload));
    }
  } catch (error) { }
};

// Staff groups. A missing endpoint or a failed request leaves the list empty, which renders
// the calendar exactly as it did before the feature — never surface an error for this.
const getStaffGroups = function* () {
  try {
    const res: AxiosResponse<{ payload: IStaffGroupResItem[] }> = yield call(apis.getStaffGroups);
    const groupsRes = res?.data?.payload;
    if (groupsRes) {
      // Ids become strings here so they match IStaffUI.id everywhere downstream.
      const data: IStaffGroupUI[] = groupsRes.map(o => ({
        id: o.id?.toString(),
        name: o.name,
        colorHex: o.colorHex,
        sortOrder: o.sortOrder,
        staffIds: (o.staffIds || []).map(id => id?.toString()),
      }));
      yield put(shopActions.staffGroups.success(data));
    }
  } catch (error) {
    // Deliberately keep the last-known groups: a transient failure should not strip the colors off
    // a calendar that is already rendering correctly. The reducer has no `fail` case for this reason.
    yield put(shopActions.staffGroups.fail(undefined));
  }
};

const createStaffGroup: ISagaFunc<IBodyCreateStaffGroup> = function* ({ payload }) {
  try {
    loadingPageSignal.value = true;
    yield call(apis.createStaffGroup, payload);
    yield call(getStaffGroups);
  } catch (error) {
    toast.error('Could not create the group. Please try again.');
  } finally {
    loadingPageSignal.value = false;
  }
};

const updateStaffGroup: ISagaFunc<{ id: string } & IBodyUpdateStaffGroup> = function* ({ payload }) {
  try {
    loadingPageSignal.value = true;
    const { id, ...body } = payload;
    yield call(apis.updateStaffGroup, id, body);
    yield call(getStaffGroups);
  } catch (error) {
    toast.error('Could not update the group. Please try again.');
  } finally {
    loadingPageSignal.value = false;
  }
};

const deleteStaffGroup: ISagaFunc<string> = function* ({ payload }) {
  try {
    loadingPageSignal.value = true;
    yield call(apis.deleteStaffGroup, payload);
    yield call(getStaffGroups);
  } catch (error) {
    toast.error('Could not delete the group. Please try again.');
  } finally {
    loadingPageSignal.value = false;
  }
};

const assignStaffGroups: ISagaFunc<IStaffGroupAssignment[]> = function* ({ payload }) {
  try {
    loadingPageSignal.value = true;
    yield call(apis.assignStaffGroups, payload);
    yield call(getStaffGroups);
  } catch (error) {
    toast.error('Could not save the staff assignments. Please try again.');
  } finally {
    loadingPageSignal.value = false;
  }
};

const initData: ISagaFunc<string> = function* ({ payload }) {
  yield storage.shopId.set(payload);
  yield delay(200);
  yield all([
    put(aptActions.getStaffByAppointment.fetch({ date: dateParamSignal.value?.format('YYYY-MM-DD') || '', page: 1, size: 10, shopId: payload })),
    put(aptActions.blockHourList.fetch()),
    put(shopActions.category.fetch(payload)),
    put(shopActions.pureStaffs.fetch(payload)),
    put(shopActions.salonInfo.fetch(payload)),
    put(shopActions.shopAllSetting.fetch(payload)),
    put(shopActions.staffGroups.fetch(undefined)),
    put(shopActions.customer.fetch(payload)),
    put(aptActions.quickBooking.getServices.fetch(payload)),
  ]);
  // @ts-ignore
  window?.ReactNativeWebView?.postMessage('WEB_READY');
  yield getSalonScheduler();
};

/**
 * Tai lai luoi cua ngay dang xem va cho toi khi xong.
 *
 * Dung chung mot endpoint voi duong doi ngay va duong socket. Truoc day ham nay
 * goi `listAppointmentRealtime`, mot endpoint rieng chi tra ve lich: luoi co lich
 * moi nhung cot tho va gio nghi van la cua ngay truoc do, va moi lan dat lich phai
 * ganh them mot request trung noi dung.
 *
 * Van phai chan — khong duoc doi thanh `put` — vi cac cho goi no tat spinner, dong
 * modal hoac cuon luoi ngay sau khi no tra ve.
 */
const getAptList = function* () {
  const staffSize: number = yield select((state: RootState) => state.apt.staffSize);
  yield loadStaffByAppointment({
    date: toCalendarDateParam(dateParamSignal.value),
    page: 1,
    size: staffSize,
  });
};
const getBlockHourList = function* () {
  const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, storage.shopId.get(), {
    startTime: dateParamSignal.value?.format('YYYY-MM-DD') || '',
    endTime: dateParamSignal.value?.format('YYYY-MM-DD') || '',
  });
  const blockHourItems = res?.data?.payload;
  if (blockHourItems) {
    const data: ICalendarItemData[] = mappingBlockHour(blockHourItems);
    yield put(aptActions.calendar.setBlockHoursData(data));
  }
};

const bookApt: ISagaFunc<IPayloadBooking> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IBookingAptResData }> = yield call(apis.bookAppointment, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      getAptList();
    }
  } catch (error: any) {
    const errorCode = get(error, 'response.data.code');
    if (errorCode === 1170) {
      aptSignal.tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
    }
  }
  finally {
    loadingSignal.value = false;
  }
};

const bookAptV2: ISagaFunc<IPayloadBookingV2> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.bookAppointmentV2, storage.shopId.get(), payload);
    const firstItem = first(payload.items);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      toast.success('Booking Success');

      const staffId = firstItem?.staffId || ANYONE_ID;
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      const currentStaffPage = staffsAvailable.find(o => o.id?.toString() === staffId?.toString())?.page;
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (firstItem?.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
    }
  } catch (error: any) {
    const errorCode = get(error, 'response.data.code');
    if (errorCode === 1170) {
      aptSignal.tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
    }
  }
  finally {
    loadingSignal.value = false;
  }
};
const addBlockHour: ISagaFunc<IBodyAddBlockHours> = function* ({ payload }) {
  loadingSignal.value = true;

  try {
    const apt: AxiosResponse<{ payload: IBlockHoursResult | boolean }> = yield call(apis.addBlockHours, payload);
    if (apt.data.payload) {
      // Older backends answer a bare `true`; the object form carries the booked
      // appointments the new time off covers.
      const result = typeof apt.data.payload === 'object' ? apt.data.payload : null;
      const warning = buildAffectedAppointmentsWarning(result?.affectedAppointments);
      if (warning) toast.info(warning, true);
      const date = moment(payload?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getBlockHourList();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const deleteApt: ISagaFunc<{ aptId: string, pinCode: string, applyScope?: 'SINGLE' | 'SERIES' }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.deleteApt, storage.shopId.get(), payload.aptId, payload.pinCode, payload.applyScope);
    if (apt.data.payload) {
      yield getAptList();
      aptSignal.close();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const repeatApt: ISagaFunc<{ appointmentId: string | number, dayRepeat: number, pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const res: AxiosResponse<{ payload: IRepeatAppointmentRes }> = yield call(
      apis.repeatAppointment, storage.shopId.get(), payload
    );
    const data = res.data.payload;
    if (!data) {
      toast.error('Repeat failed, please try again!');
      return;
    }

    // Doc ten khach truoc khi dong modal, vi close() xoa sach cac signal cua man detail.
    const customerName = aptSignal.customerSignal.value?.name;

    yield getAptList();
    aptSignal.close();

    // Bang ket qua liet ke tung buoi da tao va tung buoi bi bo qua. Buoi trung lich bi bo
    // chu khong lam hong ca thao tac, nen phai noi ro — neu khong nhan vien tuong da tao du
    // va khong bao gio kiem tra lai.
    repeatResultSignal.value = { ...data, customerName };
  } catch (error: any) {
    // Server tra loi ro nghia cho tung truong hop bi chan (chua bat cau hinh, lich da thuoc
    // chuoi, chua chon tho, lich da bat dau). Hien thang len de nhan vien biet phai lam gi.
    toast.error(get(error, 'response.data.message') || 'Repeat failed, please try again!');
    // autoToast da tat o apis.repeatAppointment nen day la toast duy nhat.
  }
  finally {
    loadingSignal.value = false;
  }
};

const cancelApt: ISagaFunc<{ aptId: string, reason: string, pinCode: string, applyScope?: 'SINGLE' | 'SERIES' }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.cancelApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      aptSignal.close();
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};
const confirmApt: ISagaFunc<{ appointmentId: string, pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.confirmApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      aptSignal.close();
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

/**
 * Doi status tu dropdown o Appointment List.
 *
 * Refetch ca bang thay vi sua tai cho mot dong: server la nguon su that, va bang con phu
 * thuoc vao filter "xem lich da huy" — doi status co the lam dong do roi ra khoi ket qua
 * dang loc.
 */
const changeAptStatus: ISagaFunc<{ appointmentId: string, status: number, pinCode: string }> = function* ({ payload }) {
  tableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(
      apis.changeAptStatus, storage.shopId.get(), payload,
    );
    if (res.data.payload) {
      // Thao tac nay bat dau tu hai cho: cot status ben danh sach, va nut go check-in tren
      // modal chi tiet lich o calendar. Nen cap nhat ca hai mat thay vi chi mat goi no.
      // upsert + close la vo hai o trang danh sach (modal khong mo, calendar khong hien),
      // con fetch lai bang la vo hai o calendar.
      yield put(aptUIActions.upsertAppointments(res.data.payload));
      aptSignal.close();
      // setParams({}) gop voi params hien tai roi fetch lai — giu nguyen trang, filter, tim kiem.
      yield put(aptActions.table.setParams({}));
      // Co tat o day: saga fetch lai se tu tat khi du lieu moi ve. Tat ngay tai day se mo
      // khoa dropdown trong luc dong van dang hien status cu, va cu bam thu hai se ban mot
      // request tu status da cu — server tu choi, nguoi dung thay loi vo co.
      return;
    }
    tableLoading.value = false;
  } catch (error) {
    // Interceptor da toast message cua server (vd. transition khong hop le).
    tableLoading.value = false;
  }
};

const checkInApt: ISagaFunc<string> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.checkInApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      aptSignal.close();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const deleteBlockHour: ISagaFunc<string> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const res = yield call(apis.removeBlockHour, payload);
    if (res.data.payload) {
      yield getBlockHourList();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};
const onFormDeleteBlockHourSuccess: ISagaFunc<string> = function* ({ payload }) {
  if (dateParamSignal.value?.format('YYYY-MM-DD') !== payload) return;
  try {
    yield getBlockHourList();
  } catch (error) { }
};

const updateApt: ISagaFunc<IPayloadBooking> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.updateApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getAptList();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const getList = function* (date: string) {
  const staffSize: number = yield select((state: RootState) => state.apt.staffSize);
  yield put(aptActions.getStaffByAppointment.fetch({ date, page: 1, size: staffSize, shopId: storage.shopId.get() }));
};

const updateAptV2: ISagaFunc<IPayloadBookingV2> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.updateAptV2, storage.shopId.get(), payload);
    const firstItem = first(payload.items);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      modalConfirmSMSChangeRef.current?.open(payload.id || '');
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      yield getList(date.format('YYYY-MM-DD'));
      aptSignal.close();
      toast.success('Update Success');
      const staffId = firstItem?.staffId || ANYONE_ID;
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      const currentStaffPage = staffsAvailable.find(o => o.id?.toString() === staffId?.toString())?.page;
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (firstItem?.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const updateBlockHour: ISagaFunc<IBodyAddBlockHours> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.updateBlockHour, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getBlockHourList();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const calendarUpdateDrop: ISagaFunc<ICalendarDropData> = function* ({ payload }) {
  loadingSignal.value = true;
  const startTime = moment(payload.startTime, 'MM-DD-YYYY HH:mm:ss');
  const body: IPayloadDragADrop = {
    payload: {
      appointmentId: +payload.id,
      startTime: startTime.format('MM-DD-YYYY HH:mm:ss'),
      endTime: startTime.clone().add(payload.distance, 'minutes').format('MM-DD-YYYY HH:mm:ss'),
      staffId: +(payload.staffId || ''),
      pinCode: payload.pinCode,
    }
  };
  try {
    const res: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.updateDragAndDrop, storage.shopId.get(), body);
    if (res.data.payload) {
      modalConfirmSMSChangeRef.current?.open(payload.id);
      yield put(aptUIActions.upsertAppointments(res.data.payload));
    }

  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const getAppointmentLists: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  tableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IAptListResPayload }> = yield call(apis.getAppointmentLists, storage.shopId.get(), payload);
    if (res?.data?.payload) {
      yield put(aptActions.table.getData.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    tableLoading.value = false;
  }
};

const getCustomerAppointmentLists: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  customerTableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IAptListResPayload }> = yield call(
      apis.getAppointmentLists,
      storage.shopId.get(),
      payload
    );
    if (res?.data?.payload) {
      yield put(aptActions.customer.getData.success(res.data.payload));
    }
  } catch (error) {
  } finally {
    customerTableLoading.value = false;
  }
};

const getAptListBlockHourList: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  blockHourTableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, storage.shopId.get(), {
      endTime: payload.end_time || '',
      staffId: payload.staffId || '',
      startTime: payload.start_time || '',
    });
    const blockHourItems = res?.data?.payload;
    if (blockHourItems) {
      yield put(aptActions.table.blockHours.success(blockHourItems));
    }
  } catch (error) {
  } finally {
    blockHourTableLoading.value = false;
  }
};
const setTableData: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  const _oldParams = yield select(tableSelectors.getPureParams);
  const params: IAppointmentListParams = {
    ..._oldParams || {},
    ...payload || {},
  };
  if (params.staffId === undefined) {
    delete params.staffId;
  }
  if (params.type === -1) {
    delete params.type;
  }
  yield put(aptActions.table.getData.fetch(params));
};

const setCustomerTableData: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  const _oldParams = yield select(customerSelectors.getPureParams);
  const params: IAppointmentListParams = {
    ...(_oldParams || {}),
    ...(payload || {}),
  };
  if (params.staffId === undefined) {
    delete params.staffId;
  }
  if (params.type === -1) {
    delete params.type;
  }
  yield put(aptActions.customer.getData.fetch(params));
};

const prolongedTime: ISagaFunc<{ id: string, status: 'ADD' | 'MINUS', pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const res: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.prolongedTime, payload.id, payload.status, payload.pinCode);
    if (res.data.payload) {
      yield put(aptUIActions.upsertAppointments(res.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const getStaffAvailableBooking: ISagaFunc<{ startTime: string, endTime: string }> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffAvailableBooking, payload);
    if (res.data.payload) {
      const data: IStaffUI[] = res.data.payload.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(aptActions.staffAvailableBooking.success(data));
    }
  } catch (error) {
    yield put(aptActions.staffAvailableBooking.fail({}));
  }
};

const prolongedTimeBlockHour: ISagaFunc<{ id: string, status: 'ADD' | 'MINUS' }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    yield call(apis.prolongedTimeBlockHour, payload.id, payload.status);
  } catch (error) { }
  finally {
    yield getBlockHourList();
    loadingSignal.value = false;
  }
};

const getSalonScheduler = function* () {
  try {
    const res: AxiosResponse<{ payload: ISchedulerSalonResData }> = yield call(apis.getSalonScheduler);
    if (res.data.payload) {
      yield put(shopActions.scheduler.success(res.data.payload));
    }
  } catch (error) {
    yield put(shopActions.scheduler.fail({}));
  }
};

const refreshCalendar = function* () {
  loadingSignal.value = true;
  yield getAptList();
  const pixel = calcTo(moment().format('MM-DD-YYYY HH:mm:ss'));
  calendarScrollTo.value(pixel - 10);
  loadingSignal.value = false;
};

const quickBookingGetServices: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IQuickBookingServiceItem[] }> = yield call(apis.getQuickBookingServices, payload);
    if (res.data.payload) {
      yield put(aptActions.quickBooking.getServices.success(res.data.payload));
    }
  } catch (error) { }
};

const doQuickBooking: ISagaFunc<IQuickBookingPayload> = function* ({ payload }) {
  loadingSignal.value = true;
  const items = payload.items;
  // Booked from the Caller v2 composer → it waits for this outcome to clear the
  // customer (success) or keep its form (failure).
  const callerRequestId = payload.callerRequestId;
  let booked = false;
  // Bao cho handler socket biet broadcast sap toi la tieng vong cua chinh thao tac
  // nay, de no khong tai lai ngay dang xem cu roi phai vut bo.
  beginLocalCalendarMutation();
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.quickBooking, storage.shopId.get(), items);
    if (apt.data.payload) {
      booked = true;
      const aptList = apt.data.payload || [];
      if (aptList?.length) {
        yield put(aptUIActions.upsertAppointments(aptList));
      }
      // Lich cua tho dau tien trong danh sach gui len — dung cho ca repeat va auto focus. Doi chieu
      // theo staffId thay vi lay phan tu dau cua response: khong co gi bao dam server tra ve dung
      // thu tu da gui.
      const firstItem = first(items);
      const createdApt = aptList.find(
        o => o.staffId?.toString() === firstItem?.staffId?.toString()
      ) || first(aptList);
      // A caller booking never opened the Quick Booking modal — leave it alone.
      if (!callerRequestId) qbSignal.close();
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      // Chuyen ca luoi sang ngay vua dat, khong rieng gi lich. Danh sach tho va gio
      // nghi deu duoc cap theo ngay: mot tho nghi ca ngay dang xem se khong co cot,
      // va lich vua dat cho tho do se khong co cho de hien.
      yield put(aptActions.calendar.getData.fetch({ startTime: date.format('YYYY-MM-DD') }));

      // Booked from a caller → that caller is handled now; refresh the pending list
      // (and close the panel if it was the last one).
      if (firstItem?.callerId) {
        yield put(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
      }

      toast.success('Booking Success');

      // Repeat la request thu hai. Neu no hong sau khi booking da thanh cong thi nhan vien
      // co lich goc nhung khong co chuoi — phai bao ro chu tuyet doi khong nuot loi, va
      // khong duoc rollback lich goc.
      if (payload.repeat) {
        // Chi tho dau tien duoc lap.
        const createdId = createdApt?.id;
        if (!createdId) {
          toast.error('Booked, but could not repeat: missing appointment id');
        } else {
          try {
            const repeatRes: AxiosResponse<{ payload: IRepeatAppointmentRes }> = yield call(
              apis.repeatAppointment, storage.shopId.get(),
              {
                appointmentId: createdId,
                dayRepeat: payload.repeat.dayRepeat,
                pinCode: payload.repeat.pinCode,
              }
            );
            // Bang ket qua thay cho toast: mot chuoi co the len toi 12 buoi, toast khong du
            // cho de liet ke va tu bien mat truoc khi doc xong.
            const repeatData = repeatRes.data.payload;
            if (repeatData) {
              repeatResultSignal.value = {
                ...repeatData,
                customerName: qbSignal.customer.value?.name,
              };
            }
          } catch (repeatError: any) {
            // Luon noi ro lich goc van con. autoToast da tat nen day la toast duy nhat;
            // neu chi hien message cua server thi nhan vien tuong ca thao tac that bai va
            // di dat lai lich.
            const serverMessage = get(repeatError, 'response.data.message');
            toast.error(
              serverMessage
                ? `Appointment booked, but repeat failed: ${serverMessage}`
                : 'Appointment booked, but repeat failed. Please repeat it from the appointment detail.'
            );
          }
          yield getAptList();
        }
      }

      const staffId = firstItem?.staffId || ANYONE_ID;
      const fullScreen: boolean = yield select((state: RootState) => state.apt.fullScreen);
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      // Phai chia trang bang dung cong thuc ma header dang dung (FULL_SCREEN chia 15 tho mot trang,
      // che do thuong 12). Lay nham `page` se nhay sang trang khong chua tho vua dat, cot bien mat
      // va khong con gi de focus.
      const currentStaffPage = getStaffCalendarPage(
        staffsAvailable.find(o => o.id?.toString() === staffId?.toString()),
        fullScreen,
      );
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      // Bam theo id that cua lich vua tao va cho toi khi card duoc render. Id o luoi
      // (`HH:mm/staffId`) chi con la duong lui: no phu thuoc dinh dang gio, khung gio mo cua va
      // thoi diem calendar render xong nen hong im lang.
      focusAppointment(
        createdApt?.id?.toString(),
        dateParamSignal?.value?.format('HH:mm') + '/' + staffId,
      );
    }
  } catch (error) { }
  finally {
    if (callerRequestId) {
      const wasCancelled: boolean = yield cancelled();
      callerBookingOutcome.value = { requestId: callerRequestId, ok: booked, cancelled: !booked && wasCancelled };
    }
    loadingSignal.value = false;
    // Chay ca khi saga bi huy: bo quen se khoa vinh vien reload cua socket.
    endLocalCalendarMutation();
  }
};

const setParamGetCommissionList: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  const _oldParams: IApiGetQuickCommissionsParams = yield select(commSelectors.getPureParams);
  const params: IApiGetQuickCommissionsParams = {
    ..._oldParams || {},
    ...payload || {},
    pageSize: 25,
  };

  if (params.staffId === '' || params.staffId === undefined) {
    delete params.staffId;
  }

  if (params.page === 1) {
    yield put(commActions.getSummaryQuickCommissions.fetch(params));
  }

  yield put(commActions.getQuickCommissions.fetch(params));
};

const getQuickCommissions = (): ISagaFunc<IApiGetQuickCommissionsParams> => function* ({ payload }) {
  yield put(commUIActions.setLoadingTable(true));
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getQuickCommissions, payload);
    if (res.data.payload) {
      yield put(commActions.getQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    yield put(commUIActions.setLoadingTable(false));
  }
};
const getSummaryQuickCommissions: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getSummaryQuickCommissions, payload);
    if (res.data.payload) {
      yield put(commActions.getSummaryQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
};

const initQuickCommissions: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  yield put(commUIActions.setLoadingTable(true));

  if (payload.page === 1) {
    yield put(commActions.getSummaryQuickCommissions.fetch(payload));
  }

  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getQuickCommissions, { ...payload, pageSize: 25 });
    if (res.data.payload) {
      yield put(commActions.getQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    yield put(commUIActions.setLoadingTable(false));
  }
};

const getOwnerReportCommissions = function* () {
  const _oldParams: IApiGetQuickCommissionsParams = yield select(commSelectors.getPureParams);
  const fromDate = moment(_oldParams.startDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  const toDate = moment(_oldParams.endDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getOwnerReportCommissions, fromDate, toDate);
    if (res.data.payload) {
      yield put(commActions.getOwnerReportCommissions.success(res.data.payload));
    }
  } catch (error) {
  }
};

function handleNativeMessage(event: MessageEvent) {
  try {
    const dataNative: { type: string, staffs: any, appointments: any, caller?: any } = JSON.parse(event?.data || '{}');
    if (dataNative?.type === 'CALENDAR_BOOTSTRAP') {
      const staffRes: IStaffResItem[] = dataNative?.staffs || [];
      if (staffRes?.length) {
        const data: IStaffUI[] = staffRes.map((o, i) => ({
          avatar: o.urlImage,
          extraData: o,
          id: o.id?.toString(),
          name: o.nickName || o.firstName,
          page: getPageNumberByIndex(i),
          pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
          timeSchedule: o.timeSchedule,
        }));
        store.dispatch(shopActions.staffAvailable.success(data));
        store.dispatch(shopActions.staff.success(data));
      }
      const appointments: IAppointmentResItem[] = dataNative?.appointments || [];
      if (appointments?.length) {
        const data: ICalendarItemData[] = mappingAppointment(appointments);
        store.dispatch(aptActions.calendar.getData.success(data));
      }
    } else if (dataNative?.type === 'CALLER_BOOKING_REQUEST') {
      if (isCallerV2Enabled()) {
        CallerV2.open();
        return;
      }
      CallerContext.onBook(dataNative.caller);
    }
  } catch (e) {
    // ignore
  }
}

const generateRNListeners = function* () {
  yield window.addEventListener('message', handleNativeMessage);
};


/**
 * Than chung cua moi duong tai luoi: doi ngay, socket, va lam moi sau khi sua lich.
 * Tra ve ca lich lan danh sach tho cua ngay duoc hoi, deu la du lieu cap theo ngay.
 */
function* loadStaffByAppointment(payload: IApiStaffByAppointmentParams) {
  const pageSize: number = yield select((state: RootState) => state.apt.staffSize);
  try {
    const res: AxiosResponse<{ payload: IApiStaffByAppointmentResData }> = yield call(apis.staffByAppointment, {
      date: payload.date,
      page: 1,
      size: payload.size || pageSize,
    });
    // Ngay dang xem da doi trong luc request bay. Dung response nay se thay nguyen
    // `calendar.data` bang du lieu ngay cu va xoa mat lich cua ngay dang xem.
    //
    // Bo im lang cung khong duoc: `takeLatest` da huy request cua ngay moi ngay khi
    // request nay duoc dispatch, nen khong con ai tai ngay dang xem nua va luoi se
    // trong cho toi lan refresh tay tiep theo. Hoi lai dung ngay hien tai.
    //
    // Khong lap vo han: request moi mang chinh ngay dang xem, chi hoi lai neu ngay
    // lai doi mot lan nua trong luc no bay.
    if (isStaleCalendarDate(payload.date, dateParamSignal.value)) {
      yield put(aptActions.getStaffByAppointment.fetch({
        ...payload,
        date: toCalendarDateParam(dateParamSignal.value),
      }));
      return;
    }
    if (res.data.payload) {
      yield put(aptActions.getStaffByAppointment.success(res.data.payload));
      if (res.data.payload.total > payload.size) {
        yield put(aptActions.getStaffByAppointment.fetchPaging({ date: payload.date, page: 1, size: res.data.payload.total }));
      }
    }
  } catch (error) {
  }
}

const getStaffByAppointment: ISagaFunc<IApiStaffByAppointmentParams> = function* ({ payload }) {
  yield loadStaffByAppointment(payload);
};

const getStaffByAppointmentFetchPaging: ISagaFunc<IApiStaffByAppointmentParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IApiStaffByAppointmentResData }> = yield call(apis.staffByAppointment, payload);
    if (isStaleCalendarDate(payload.date, dateParamSignal.value)) return;
    if (res.data.payload) {
      yield put(aptActions.getStaffByAppointment.success(res.data.payload));
    }
  } catch (error) {
  }
};

const getClockInStaff = function* () {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffs, storage.shopId.get());
    if (res.data.payload) {
      yield put(shopActions.clockInStaffs.success(res.data.payload.filter(o => !!o.isLockedin)));
    }
  } catch (error) {
  }
};

const getCallerIdHistory: ISagaFunc<IApiHistoryCallerIdParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: ICallerIdHistoryResData }> = yield call(apis.historyCallerId, payload);
    if (res.data.payload) {
      yield put(aptActions.getCallerIdHistory.success(res.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCallerIdHistory.fail({}));
  }
};

// Caller V2 — today's unhandled callers. Fires on each panel open/trigger.
const getPendingCallers: ISagaFunc<{ closeIfEmpty?: boolean } | undefined> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IPendingCallerResItem[] }> = yield call(apis.getPendingCallers);
    const data = res.data.payload || [];
    yield put(aptActions.getPendingCallers.success(data));
    // Booked/SMS'd the last pending caller → nothing left to handle, close the panel.
    if (payload?.closeIfEmpty && data.length === 0) {
      CallerV2.close();
    }
  } catch (error) {
    yield put(aptActions.getPendingCallers.fail({}));
  }
};

// Caller V2 — customer brief + up to 2 recent past appointments (detail tab).
const getCustomerRecent: ISagaFunc<number> = function* ({ payload: customerId }) {
  try {
    const res: AxiosResponse<{ payload: ICustomerRecentApptsResData }> = yield call(apis.getCustomerRecentAppointments, customerId);
    if (res.data.payload) {
      yield put(aptActions.getCustomerRecent.success(res.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCustomerRecent.fail({}));
  }
};

// Caller V2 — paginated full appointment history (booking-history modal).
const getCustomerAppointments: ISagaFunc<ICustomerApptsPageParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: ICustomerApptsPageResData }> = yield call(apis.getCustomerAppointments, payload);
    if (res.data.payload) {
      yield put(aptActions.getCustomerAppointments.success({ ...res.data.payload, customerId: payload.customerId }));
    }
  } catch (error) {
    yield put(aptActions.getCustomerAppointments.fail({}));
  }
};

const getStaffByAppointmentSuccess: ISagaFunc<IApiStaffByAppointmentResData> = function* ({ payload }) {
  const dataApt: ICalendarItemData[] = mappingAppointment(payload.data.flatMap(o => o.appointments));
  yield put(aptActions.calendar.getData.success(dataApt));
  const dataStaff: IStaffUI[] = payload.data.map((o, i) => ({
    avatar: o.urlImage,
    extraData: o,
    id: o.id?.toString(),
    name: o.nickName || o.firstName,
    page: getPageNumberByIndex(i),
    pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
    timeSchedule: o.timeSchedule,
  }));
  yield put(shopActions.staffAvailable.success(dataStaff));
};

const getAppointmentSummaryCallerId: ISagaFunc<IAppointmentSummaryCallerIdParams> = function* ({ payload }) {
  try {
    const resData = yield call(apis.getAppointmentSummaryCallerId, payload);
    if (resData?.data?.payload) {
      yield put(aptActions.getAppointmentSummaryCallerId.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getAppointmentSummaryCallerId.fail({}));
  } finally {
  }
};

const getCustomerTransactions: ISagaFunc<number> = function* ({ payload }) {
  try {
    const resData = yield call(apis.getCustomerTransactions, payload);
    if (resData?.data?.payload) {
      yield put(aptActions.getCustomerTransactions.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCustomerTransactions.fail({}));
  } finally {
  }
};

const getShopSurveyInfo: ISagaFunc = function* () {
  try {
    const resData = yield call(apis.getShopSurveyInfo);
    if (resData?.data?.payload) {
      yield put(aptActions.getShopSurveyInfo.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getShopSurveyInfo.fail({}));
  } finally {
  }
};


export default function* aptSagas() {
  yield takeLatest(aptActions.calendar.getData.fetch, getCalendarData);
  yield takeLatest(aptActions.calendar.refresh, refreshCalendar);
  yield takeLatest(aptActions.initData, initData);
  yield takeLatest(aptActions.calendar.updateDrop.fetch, calendarUpdateDrop);
  yield takeLatest(aptActions.bookApt, bookApt);
  yield takeLatest(aptActions.bookAptV2, bookAptV2);
  yield takeLatest(aptActions.deleteApt, deleteApt);
  yield takeLatest(aptActions.repeatApt, repeatApt);
  yield takeLatest(aptActions.cancelApt, cancelApt);
  yield takeLatest(aptActions.confirmApt, confirmApt);
  yield takeLatest(aptActions.changeAptStatus, changeAptStatus);
  yield takeLatest(aptActions.checkInApt, checkInApt);
  yield takeLatest(aptActions.updateApt, updateApt);
  yield takeLatest(aptActions.updateAptV2, updateAptV2);
  yield takeLatest(aptActions.table.getData.fetch, getAppointmentLists);
  yield takeLatest(aptActions.table.getData.fetch, getAptListBlockHourList);
  yield takeLatest(aptActions.table.setParams, setTableData);
  yield takeLatest(aptActions.customer.getData.fetch, getCustomerAppointmentLists);
  yield takeLatest(aptActions.customer.setParams, setCustomerTableData);
  yield takeLatest(aptActions.customer.initData, setCustomerTableData);
  yield takeLatest(aptActions.calendar.prolongedTime, prolongedTime);
  yield takeLatest(aptActions.calendar.getData.fetch, getCalendarBlockHourList);
  yield takeLatest(aptActions.deleteBlockHour, deleteBlockHour);
  yield takeLatest(aptActions.onFormDeleteBlockHourSuccess, onFormDeleteBlockHourSuccess);
  yield takeLatest(aptActions.addBlockHour, addBlockHour);
  yield takeLatest(aptActions.updateBlockHour, updateBlockHour);
  yield takeLatest(aptActions.prolongedTimeBlockHour, prolongedTimeBlockHour);
  yield takeLatest(aptActions.quickBooking.getServices.fetch, quickBookingGetServices);
  yield takeLatest(aptActions.quickBooking.doQuickBooking, doQuickBooking);
  yield takeLatest(shopActions.staff.fetch, getStaffActions);
  yield takeLatest(shopActions.pureStaffs.fetch, getPureStaffs);
  yield takeLatest(shopActions.staffAvailable.fetch, getStaffsAvailable);
  yield takeLatest(shopActions.blockHourHistories.fetch, getBlockHourHistories);
  yield takeLatest(shopActions.timeOffList.fetch, getTimeOffList);
  yield takeLatest(shopActions.scheduler.fetch, getSalonScheduler);
  yield takeLatest(shopActions.category.fetch, getCategories);
  yield takeLatest(shopActions.customer.fetch, getCustomers);
  yield takeLatest(shopActions.salonInfo.fetch, getSalonInfo);
  yield takeLatest(shopActions.shopAllSetting.fetch, getShopAllSetting);
  yield takeLatest(shopActions.staffGroups.fetch, getStaffGroups);
  yield takeLatest(shopActions.createStaffGroup, createStaffGroup);
  yield takeLatest(shopActions.updateStaffGroup, updateStaffGroup);
  yield takeLatest(shopActions.deleteStaffGroup, deleteStaffGroup);
  yield takeLatest(shopActions.assignStaffGroups, assignStaffGroups);
  yield takeLatest(aptActions.staffAvailableBooking.fetch, getStaffAvailableBooking);
  yield takeLatest(commActions.setParams, setParamGetCommissionList);
  yield takeLatest(commActions.init, initQuickCommissions);
  yield takeLatest(commActions.getSummaryQuickCommissions.fetch, getSummaryQuickCommissions);
  yield takeLatest(commActions.getQuickCommissions.fetch, getQuickCommissions());
  yield takeLatest(commActions.getQuickCommissions.fetchPaging, getQuickCommissions());
  yield takeLatest(commActions.getOwnerReportCommissions.fetch, getOwnerReportCommissions);
  yield takeLatest(aptActions.getStaffByAppointment.fetch, getStaffByAppointment);
  yield takeLatest(aptActions.getStaffByAppointment.success, getStaffByAppointmentSuccess);
  yield takeLatest(aptActions.getStaffByAppointment.fetchPaging, getStaffByAppointmentFetchPaging);
  yield takeLatest(shopActions.clockInStaffs.fetch, getClockInStaff);
  yield takeLatest(aptActions.getCallerIdHistory.fetch, getCallerIdHistory);
  yield takeLatest(aptActions.getPendingCallers.fetch, getPendingCallers);
  yield takeLatest(aptActions.getCustomerRecent.fetch, getCustomerRecent);
  yield takeLatest(aptActions.getCustomerAppointments.fetch, getCustomerAppointments);
  yield takeLatest(aptActions.getAppointmentSummaryCallerId.fetch, getAppointmentSummaryCallerId);
  yield takeLatest(aptActions.blockHourList.fetch, getBlockHourList);
  yield takeLatest(aptActions.getCustomerTransactions.fetch, getCustomerTransactions);
  yield takeLatest(aptActions.getShopSurveyInfo.fetch, getShopSurveyInfo);
  yield takeLatest(shopActions.scheduleTimeOffSlots.fetch, getScheduleTimeOffSlots);
  yield generateRNListeners();
}
